import {usePrefersReducedMotion} from '@github-ui/brand-core/hooks/usePrefersReducedMotion'
import {hasWebGLSupport} from '@github-ui/brand-core/utils/platform'
import {isLazyOcticonName, LazyOcticon} from '@github-ui/lazy-octicon'
import {useMediaQuery} from '@github-ui/use-media-query'
import {PlusCircleIcon, XIcon} from '@primer/octicons-react'
import {Button, Text, useTheme} from '@primer/react'
import {clsx} from 'clsx'
import {AnimatePresence, motion} from 'motion/react'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import type {
  ProductAnnouncementBannerProps as ProductAnnouncementBannerPropsBase,
  ProductAnnouncementBannerVisual,
} from './../ProductAnnouncement/ProductAnnouncement.types'
import ProductAnnouncementBannerAnimation from './ProductAnnouncementBannerAnimation/ProductAnnouncementBannerAnimation'

import Styles from './ProductAnnouncementBanner.module.css'

export type ProductAnnouncementBannerProps = ProductAnnouncementBannerPropsBase

const defaultProps: Partial<ProductAnnouncementBannerProps> = {
  // The default is known here, so import it rather than paying for a chunk
  // request to resolve a name we already have. Only a caller-supplied name is
  // resolved lazily.
  labelIcon: PlusCircleIcon,
  isVisible: true,
}

/** A name is resolved lazily so callers do not pull in the whole octicons set; a component is rendered as-is. */
const renderLabelIcon = (labelIcon: ProductAnnouncementBannerProps['labelIcon']) => {
  if (!labelIcon) return null

  const className = Styles['ProductAnnouncementBanner__content-labelIcon']

  if (typeof labelIcon === 'string') {
    if (!isLazyOcticonName(labelIcon)) return null
    return <LazyOcticon name={labelIcon} className={className} />
  }

  const LabelIcon = labelIcon
  return <LabelIcon className={className} />
}

const ProductAnnouncementBanner = (props: ProductAnnouncementBannerProps) => {
  const initializedProps: ProductAnnouncementBannerProps = {...defaultProps, ...props}
  const {
    variant,
    title,
    label,
    labelIcon,
    visual,
    webGLSceneId,
    domAnimationSceneId,
    onClick,
    onDismiss,
    aria,
    isVisible,
    onEvent,
  } = initializedProps

  const isNudge = variant === 'nudge'
  const [hasLoadedVisual, setHasLoadedVisual] = useState<boolean>(false)
  const bannerRef = useRef<HTMLDivElement>(null)
  const {colorScheme} = useTheme()
  const isDesktopView = useMediaQuery('(min-width: 768px)')

  // The label is a flex row with no reserved slot for the icon, so a lazily
  // resolved icon keeps LazyOcticon's correctly-sized placeholder — otherwise the
  // label text shifts sideways the moment the chunk lands.
  const labelIconElement = renderLabelIcon(labelIcon)

  const shouldReduceMotion = usePrefersReducedMotion()
  const shouldUseWebGL = useMemo(
    () => Boolean(webGLSceneId) && !shouldReduceMotion && hasWebGLSupport(),
    [webGLSceneId, shouldReduceMotion],
  )
  const shouldUseDomAnimation = useMemo(
    () => !shouldUseWebGL && Boolean(domAnimationSceneId) && !shouldReduceMotion,
    [domAnimationSceneId, shouldReduceMotion, shouldUseWebGL],
  )

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const colorMode = useMemo(() => (['dark', 'night'].includes(colorScheme!) ? 'dark' : 'light'), [colorScheme])
  // Use this as array dependency since React doesn't deeply compare objects...
  const visualKey = useMemo(() => JSON.stringify(visual), [visual])

  const getVisualSrc = useCallback(
    (src: ProductAnnouncementBannerVisual['src']) => {
      if (typeof src === 'string') return src
      return colorMode === 'dark' ? src.dark : src.light
    },
    [colorMode],
  )

  const handleOnClick = useCallback(() => {
    onEvent?.('clicked')
    onClick()
  }, [onClick, onEvent])

  const syncVisualForKey = useEffectEvent(() => {
    if (!visual) return
    if (!isVisible) {
      setHasLoadedVisual(false)
      return
    }

    const loadImages = async () => {
      if ('desktop' in visual) {
        const imgDesktop = new Image()
        imgDesktop.src = getVisualSrc(visual.desktop.src)

        const imgMobile = new Image()
        imgMobile.src = getVisualSrc(visual.mobile.src)

        try {
          await Promise.all([imgDesktop.decode(), imgMobile.decode()])
        } catch {
          /* Ignore errors */
        }

        setHasLoadedVisual(true)
      } else {
        const img = new Image()
        img.src = getVisualSrc(visual.src)
        img.onload = () => setHasLoadedVisual(true)
      }
    }

    void loadImages()
  })

  // Re-sync whenever the serialized visual or visibility changes. The event reads
  // the latest `visual`/`getVisualSrc`/`onEvent` without making them reactive.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    syncVisualForKey()
  }, [visualKey, isVisible])

  const reportDisplayed = useEffectEvent(() => onEvent?.('displayed'))
  useEffect(() => {
    if (!isVisible) return
    const element = bannerRef.current
    if (!element) return
    let reported = false
    const reportWhenDisplayed = () => {
      if (reported || element.getClientRects().length === 0) return
      reported = true
      reportDisplayed()
    }
    reportWhenDisplayed()
    const observer = new ResizeObserver(reportWhenDisplayed)
    observer.observe(element)
    return () => observer.disconnect()
  }, [isVisible])

  /* TODO: Implement WebGL scene
  useEffect(() => {
    if (shouldUseWebGL && isVisible && !webGLScene.current) {
      setHasLoadedVisual(false)
      webGLScene.current = new BannerScene({
        canvas: canvasRef.current!,
        sceneId: webGLSceneId,
        colorMode,
        onLoad: () => setHasLoadedVisual(true),
      })
    } else if (!isVisible && webGLScene.current) {
      setHasLoadedVisual(false)
      webGLScene.current.destroy()
      webGLScene.current = null
    }
  }, [shouldUseWebGL, webGLSceneId, isVisible])
  */

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={bannerRef}
          {...(!shouldReduceMotion && {
            initial: {opacity: 0, x: 40},
            animate: {opacity: 1, x: 0},
            exit: {opacity: 0, x: 40},
            transition: {duration: 0.3},
          })}
          className={clsx(Styles['ProductAnnouncementBanner'], isNudge && Styles['ProductAnnouncementBanner--nudge'])}
          data-testid="product-announcement-banner"
        >
          {!shouldUseWebGL && visual && (
            <div
              className={clsx(
                Styles['ProductAnnouncementBanner__visual'],
                !hasLoadedVisual && Styles['ProductAnnouncementBanner__visual--hidden'],
                isNudge && Styles['ProductAnnouncementBanner__visual--inline'],
              )}
              style={{
                ['--offset-x-desktop']: 'desktop' in visual ? visual.desktop.offsetX || '0' : visual.offsetX || '0',
                ['--offset-y-desktop']: 'desktop' in visual ? visual.desktop.offsetY || '0' : visual.offsetY || '0',
                ['--offset-x-mobile']: 'desktop' in visual ? visual.mobile.offsetX || '0' : visual.offsetX || '0',
                ['--offset-y-mobile']: 'desktop' in visual ? visual.mobile.offsetY || '0' : visual.offsetY || '0',
              }}
            >
              {'desktop' in visual ? (
                <picture>
                  <source media="(width < 768px)" srcSet={getVisualSrc(visual.mobile.src)} />
                  <img
                    src={getVisualSrc(visual.desktop.src)}
                    alt={isDesktopView ? visual.desktop.alt : visual.mobile.alt}
                    loading="eager"
                    fetchPriority="high"
                  />
                </picture>
              ) : isNudge ? (
                <span
                  className={Styles['ProductAnnouncementBanner__splay']}
                  style={{['--splay-count']: visual.splayColors?.length ?? 0}}
                >
                  {isNudge &&
                    visual.splayColors?.map((color, index) => (
                      <span
                        key={color}
                        aria-hidden="true"
                        className={Styles['ProductAnnouncementBanner__splay-ghost']}
                        style={{
                          '--splay-index': index + 1,
                          backgroundColor: color,
                          maskImage: `url(https://github.githubassets.com/%22$%7BgetVisualSrc(visual.src)}")`,
                        }}
                      />
                    ))}
                  <img src={getVisualSrc(visual.src)} alt={visual.alt} loading="eager" fetchPriority="high" />
                </span>
              ) : (
                <img src={getVisualSrc(visual.src)} alt={visual.alt} loading="eager" fetchPriority="high" />
              )}

              {shouldUseDomAnimation && (
                <div className={Styles['ProductAnnouncementBanner__visual-animation']}>
                  <ProductAnnouncementBannerAnimation sceneId={domAnimationSceneId ?? ''} />
                </div>
              )}
            </div>
          )}

          {shouldUseWebGL && (
            <div
              className={clsx(
                Styles['ProductAnnouncementBanner__canvas'],
                !hasLoadedVisual && Styles['ProductAnnouncementBanner__canvas--hidden'],
              )}
            >
              {/* <canvas ref={canvasRef} /> */}
            </div>
          )}

          <div className={Styles['ProductAnnouncementBanner__content']}>
            <Text size="large" weight="semibold" className={Styles['ProductAnnouncementBanner__content-title']}>
              {title}
            </Text>
            {isNudge ? (
              <Button variant="primary" onClick={handleOnClick}>
                {label}
              </Button>
            ) : (
              <Button
                variant="invisible"
                className={Styles['ProductAnnouncementBanner__content-label']}
                onClick={handleOnClick}
              >
                {labelIconElement}
                {label}
              </Button>
            )}
          </div>

          <div className={Styles['ProductAnnouncementBanner__close']}>
            <button
              aria-label={aria.close}
              onClick={event => {
                event.stopPropagation()
                onEvent?.('dismissed')
                onDismiss()
              }}
            >
              <XIcon />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ProductAnnouncementBanner
